import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { activateOrder, emergencyQuota, getMe, listPoOrders } from "../lib/hostApi";
import { rupiah } from "../lib/format";
import { Shell } from "./host";
import { useT } from "../lib/i18n";
import { LangToggle } from "../components/LangToggle";

/** Panel admin. Produksi: dilindungi Cloudflare Access, bukan login sendiri. */
export const Route = createFileRoute("/admin")({ component: AdminPage });

function AdminPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: getMe, retry: false });

  const orders = useQuery({ queryKey: ["admin-orders"], queryFn: listPoOrders, enabled: me.data?.role === "admin" });
  const activate = useMutation({
    mutationFn: activateOrder,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-orders"] }),
  });

  if (me.isPending) return <Shell>{t("common.loading")}</Shell>;
  if (me.data?.role !== "admin") return <Shell>{t("admin.forbidden")}</Shell>;

  return (
    <Shell>
      <div className="flex items-center justify-between gap-2">
        <h1 className="font-serif text-2xl text-ink">{t("admin.title")}</h1>
        <LangToggle />
      </div>

      <section className="space-y-2">
        <h2 className="font-medium">{t("admin.po_pending_heading")}</h2>
        {orders.data?.items.length === 0 && <p className="text-sm text-neutral-500">{t("admin.po_empty")}</p>}
        <ul className="card divide-y divide-sand/60 p-0">
          {orders.data?.items.map((o) => (
            <li key={o.id} className="flex items-center gap-2 px-3 py-2 text-sm">
              <span className="flex-1 truncate">
                {o.title ?? o.slug} · {o.email}
              </span>
              <span className="text-neutral-500">{rupiah(o.amount)}</span>
              <button
                type="button"
                className="btn-primary px-3 py-1 text-sm"
                disabled={activate.isPending}
                onClick={() => activate.mutate(o.id)}
              >
                {t("admin.activate")}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <EmergencyQuota />
    </Shell>
  );
}

/** Tombol darurat: gateway QRIS mati saat acara berlangsung. */
function EmergencyQuota() {
  const { t } = useT();
  const [id, setId] = useState("");
  const [photos, setPhotos] = useState("0");
  const [devices, setDevices] = useState("0");
  const add = useMutation({
    mutationFn: () => emergencyQuota(id, { photos: Number(photos) || 0, devices: Number(devices) || 0 }),
  });

  return (
    <form
      className="card space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      <h2 className="font-medium">{t("admin.emergency_heading")}</h2>
      <div className="flex gap-2">
        <input
          className="field w-24 px-2 py-1"
          placeholder={t("admin.id_placeholder")}
          value={id}
          onChange={(e) => setId(e.target.value)}
        />
        <input
          className="field w-24 px-2 py-1"
          type="number"
          min={0}
          placeholder={t("admin.photos_placeholder")}
          value={photos}
          onChange={(e) => setPhotos(e.target.value)}
        />
        <input
          className="field w-24 px-2 py-1"
          type="number"
          min={0}
          placeholder={t("admin.guests_placeholder")}
          value={devices}
          onChange={(e) => setDevices(e.target.value)}
        />
        <button type="submit" className="btn-primary px-3 py-1 text-sm" disabled={!id || add.isPending}>
          {t("admin.add")}
        </button>
      </div>
      {add.error && <p className="text-sm text-red-600">{add.error.message}</p>}
      {add.data && (
        <p className="text-sm text-green-700">
          {t("admin.quota_result", { photos: add.data.photo_quota, guests: add.data.device_quota })}
        </p>
      )}
    </form>
  );
}
